import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, type Profile } from '@/lib/supabase';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileError: string | null;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);

  const fetchProfile = (userId: string) => {
    setProfileError(null);
    supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('Profile fetch error:', error);
          setProfileError(error.message || '프로필 조회 중 오류가 발생했습니다.');
          setLoading(false);
          return;
        }
        if (data) {
          setProfile(data as Profile);
        } else {
          setProfileError('프로필 정보가 데이터베이스에 없습니다.');
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        console.error('Profile fetch exception:', err);
        const msg = err instanceof Error ? err.message : '프로필 조회 중 예기치 않은 오류가 발생했습니다.';
        setProfileError(msg);
        setLoading(false);
      });
  };

  useEffect(() => {
    let active = true;

    const clearAuth = () => {
      if (!active) return;
      setSession(null);
      setProfile(null);
      setProfileError(null);
      setLoading(false);
    };

    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (!active) return;
        // 리프레시 토큰이 만료/무효화된 경우 → 로컬 세션만 정리하고 로그인 화면으로
        if (error) {
          console.error('세션 복원 실패:', error.message);
          supabase.auth.signOut({ scope: 'local' }).finally(clearAuth);
          return;
        }
        setSession(data.session);
        if (data.session) {
          fetchProfile(data.session.user.id);
        } else {
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        console.error('세션 복원 예외:', err);
        supabase.auth.signOut({ scope: 'local' }).finally(clearAuth);
      });

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setProfile(null);
        setProfileError(null);
        setLoading(false);
        return;
      }
      // 토큰 자동 갱신(TOKEN_REFRESHED)은 세션만 갱신하고 loading을 건드리지 않는다.
      // loading을 true로 바꾸면 RequireAuth가 children을 언마운트해 진행 중이던 검사가 처음으로 초기화된다.
      if (event === 'TOKEN_REFRESHED') {
        setSession(newSession);
        return;
      }
      setSession(newSession);
      if (newSession) {
        setLoading(true);
        fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
        setProfileError(null);
        setLoading(false);
      }
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      // 서버 로그아웃 실패 시에도 로컬 세션은 정리
      await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
    }
    setProfile(null);
    setProfileError(null);
    setSession(null);
  };

  const refreshProfile = async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      setLoading(true);
      fetchProfile(data.session.user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        loading,
        profileError,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}